import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Performance } from '@jfw/angular/shared/firebase/performance';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import {
  EcommerceCoreModule,
  NavigationService,
} from '@jfw/angular/ecommerce/core';
import { ImpersonationBarComponent } from '@jfw/angular/shared/common-components/impersonation-bar';
import { AccountService } from '@jfw/angular/shared/services';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { environment } from '../environments/environment';
import { EventSearchComponent } from './components/event/search/event-search.component';
import { HeaderComponent } from './components/header/header.component';
import { CustomizeLookFilterSidenavComponent } from './components/sidenavs/customize-look-filter/customize-look-filter.component';
import { HeaderSidenavComponent } from './components/sidenavs/header/header.component';
import { PlpFilterSidenavComponent } from './components/sidenavs/plp-filter/plp-filter.component';
import { PageViewTrackingService } from './services/analytics/page-view-tracking.service';
import { AuthService } from './services/auth/auth.service';
import { DealerAccountService } from './services/dealer-account/dealer-account.service';
import { DealerUserService } from './services/dealer-user/dealer-user.service';
import { ModuleAccessService } from './services/module-access/module-access.service';

enum Sidenavs {
  Header = 'Header',
  Search = 'Search',
  CustomizeLook = 'CustomizeLook',
  Plp = 'Plp',
  None = 'None',
}

// deploy 111

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CustomizeLookFilterSidenavComponent,
    EcommerceCoreModule,
    EventSearchComponent,
    HeaderComponent,
    HeaderSidenavComponent,
    ImpersonationBarComponent,
    MatSidenavModule,
    NgTemplateOutlet,
    PlpFilterSidenavComponent,
    RouterModule,
  ],
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private breakpointObserver = inject(BreakpointObserver);
  private performance = inject(Performance); // needed for Firebase performance monitoring
  private accountService = inject(AccountService);
  private dealerAccountService = inject(DealerAccountService);
  private dealerUserService = inject(DealerUserService);
  private moduleAccessService = inject(ModuleAccessService);
  private pageViewTracking = inject(PageViewTrackingService); // EXT-6071: eager construction starts the NavigationEnd subscription at bootstrap

  private dealerAccount = toSignal(
    this.dealerAccountService.currentDealerAccount$,
    { initialValue: null },
  );

  protected maintenance = environment.maintenance;

  private firebaseUser$ = this.authService.firebaseUser$;

  protected firebaseUser = toSignal(this.firebaseUser$);

  // EXT-6617: the impersonation bar, at the bottom of every page. This is the
  // only subscriber to impersonationTimeLeft$, whose timer isn't shared: a
  // second one would sign out and show "Impersonation Ended" twice.
  protected readonly impersonationMode = toSignal(
    this.authService.impersonationMode$,
    { initialValue: false },
  );
  protected readonly impersonatingUser = toSignal(
    this.authService.impersonatingUser$,
    { initialValue: null },
  );
  protected readonly impersonationTimeLeft = toSignal(
    this.authService.impersonationTimeLeft$,
    { initialValue: null },
  );

  protected onExitImpersonation(): void {
    this.authService.exitImpersonation();
  }

  // EXT-6277: the header, sidenav and routed pages all branch on the dealer
  // user (newSite, role) and its account (ecomStatus), so rendering them before
  // both exist drew the legacy variant first and then redrew. The shell waits
  // on real readiness flags instead -- mirroring Platform Portal's loader --
  // and names whichever step is still outstanding.
  private static readonly LOADING_STEPS = [
    'Signing you in…',
    'Fetching your profile…',
    'Loading your account…',
  ] as const;

  // Last-resort release: if a request hangs past this, render the shell
  // anyway rather than leave the dealer on the loading screen. Real failures
  // (no enabled dealers, API errors) already sign out well before this.
  private static readonly READY_TIMEOUT_MS = 60_000;

  private readonly dealerUserLoaded$ =
    this.dealerUserService.currentDealerUser$.pipe(
      map((dealerUser) => dealerUser !== null),
    );

  private readonly dealerAccountLoaded$ =
    this.dealerAccountService.currentDealerAccount$.pipe(
      map((dealerAccount) => dealerAccount !== null),
    );

  // Re-evaluated per sign-in, and latched with take(1) once both have loaded,
  // so an account switch (which re-emits both) never brings the loader back.
  // Signed out, or signed in with an unverified email, there is nothing to
  // wait on -- sign-in, email-not-verified etc. render exactly as before.
  //
  // The impersonate route is excluded outright: it drives its own spinner,
  // and signInWithCustomToken() signs a user in while ImpersonateUserComponent
  // is mid-flow. Gating there would swap the shell for the loader, destroying
  // <router-outlet> and the component with it; the recreated instance reads
  // the already-stripped token query param and fails impersonation. Mapped to
  // a distinct boolean so ordinary navigations don't restart the gate.
  private readonly isImpersonateRoute$ =
    this.authService.currentRouteConfig$.pipe(
      map((routeConfig) => routeConfig === '/impersonate'),
      startWith(false),
      distinctUntilChanged(),
    );

  private readonly appReady$ = combineLatest([
    this.firebaseUser$,
    this.isImpersonateRoute$,
  ]).pipe(
    switchMap(([firebaseUser, isImpersonateRoute]) => {
      if (!firebaseUser?.emailVerified || isImpersonateRoute) {
        return of(true);
      }
      return combineLatest([
        this.dealerUserLoaded$,
        this.dealerAccountLoaded$,
      ]).pipe(
        filter(([userLoaded, accountLoaded]) => userLoaded && accountLoaded),
        take(1),
        map(() => true),
        timeout(AppComponent.READY_TIMEOUT_MS),
        catchError((error) => {
          console.error('Dealer Portal did not finish loading', error);
          return of(true);
        }),
        startWith(false),
      );
    }),
  );

  protected readonly appReady = toSignal(this.appReady$, {
    initialValue: false,
  });

  // Account switch: the same loader, laid over the shell rather than
  // replacing it -- the switch's own write runs inside HeaderComponent, so
  // unmounting the header mid-switch would cancel it. Up from the moment a
  // dealer is picked until that dealer's account has loaded; cleared early if
  // the switch fails or the user is signed out.
  private static readonly SWITCH_TIMEOUT_MS = 30_000;

  private readonly accountSwitching$ = this.firebaseUser$.pipe(
    switchMap((firebaseUser) => {
      if (!firebaseUser) {
        return of(false);
      }
      return this.dealerUserService.newDefaultDealerId$.pipe(
        switchMap((dealerId) =>
          merge(
            this.dealerAccountService.currentDealerAccount$.pipe(
              filter(
                (account) => account?.accountNumber?.trim() === dealerId.trim(),
              ),
            ),
            this.dealerUserService.defaultDealerSwitchFailed$,
          ).pipe(
            take(1),
            map(() => false),
            timeout(AppComponent.SWITCH_TIMEOUT_MS),
            catchError((error) => {
              console.error('Account switch did not finish loading', error);
              return of(false);
            }),
            startWith(true),
          ),
        ),
        startWith(false),
      );
    }),
  );

  protected readonly accountSwitching = toSignal(this.accountSwitching$, {
    initialValue: false,
  });

  private readonly authReady = toSignal(
    this.firebaseUser$.pipe(map(() => true)),
    { initialValue: false },
  );
  private readonly profileReady = toSignal(this.dealerUserLoaded$, {
    initialValue: false,
  });
  private readonly accountReady = toSignal(this.dealerAccountLoaded$, {
    initialValue: false,
  });

  protected readonly loadingStepIndex = computed(() => {
    if (!this.authReady()) return 0;
    if (!this.profileReady()) return 1;
    if (!this.accountReady()) return 2;
    return 3;
  });

  // Clamped: the index can reach 3 an instant before appReady swaps the
  // loader out.
  protected readonly loadingLabel = computed(() => {
    const index = Math.min(this.loadingStepIndex(), 2);
    return AppComponent.LOADING_STEPS[index];
  });

  protected readonly switchingLabel = 'Getting your account details…';

  protected readonly loadingSteps = AppComponent.LOADING_STEPS;

  private isFullScreen$ = this.authService.currentRouteConfig$.pipe(
    map((currentRouteConfig) => {
      const fullScreenRoutes = ['/ensembles/:category', '/support'];

      // Special Case for '/event/*' pages (make them full screen to start)
      if (currentRouteConfig.startsWith('/event/')) {
        return true;
      }

      return fullScreenRoutes.includes(currentRouteConfig);
    }),
  );

  protected isFullScreen = toSignal(this.isFullScreen$);

  // DO NOT DELETE - search drawer will be reenabled in future
  // private drawerWidth$ = this.navigationService.showSearchDrawer$.pipe(
  //   map((showSearchDrawer) => (showSearchDrawer ? 308 : 48)),
  // );

  // protected drawerWidth = toSignal(this.drawerWidth$);

  private sidenav = viewChild(MatSidenav);
  protected Sidenavs = Sidenavs;

  private showHeaderNav$ = this.navigationService.showHeaderNav$.pipe(
    map((show) => (show ? Sidenavs.Header : Sidenavs.None)),
  );

  private showSearchNav$ = this.navigationService.showSearchNav$.pipe(
    map((show) => (show ? Sidenavs.Search : Sidenavs.None)),
  );

  private showCustomizeLookNav$ =
    this.navigationService.showCustomizeLookNav$.pipe(
      map((show) => (show ? Sidenavs.CustomizeLook : Sidenavs.None)),
    );

  private showPlpNav$ = this.navigationService.showPlpNav$.pipe(
    map((show) => (show ? Sidenavs.Plp : Sidenavs.None)),
  );

  private currentSidenav$ = merge(
    this.showHeaderNav$,
    this.showSearchNav$,
    this.showCustomizeLookNav$,
    this.showPlpNav$,
  ).pipe(
    tap((sidenav) => {
      // use setTimeout to allow time for sidenav position to be set before animation
      if (sidenav === Sidenavs.None) {
        setTimeout(() => this.sidenav()?.close());
      } else {
        setTimeout(() => this.sidenav()?.open());
      }
    }),
    shareReplay(1),
  );

  protected currentSidenav = toSignal(this.currentSidenav$);

  // header sidenav is on left side (start); all others on right side (end)
  private sidenavPosition$ = this.currentSidenav$.pipe(
    filter((sidenav) => sidenav !== Sidenavs.None),
    map((sidenav) => (sidenav === Sidenavs.Header ? 'start' : 'end')),
  );

  protected sidenavPosition = toSignal(this.sidenavPosition$, {
    initialValue: 'start',
  });

  // EXT-6571: the newSite desktop header runs out of room above CDK's Small
  // breakpoint -- on a tablet its account details were cut off at the top --
  // so newSite dealers get the mobile header up to this width. Legacy dealers
  // share the header and keep the Small breakpoint.
  private static readonly NEW_SITE_MOBILE_HEADER_QUERY =
    '(max-width: 1279.98px)';

  private isMobile$ = combineLatest([
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]),
    this.breakpointObserver.observe(AppComponent.NEW_SITE_MOBILE_HEADER_QUERY),
    this.moduleAccessService.newSite$.pipe(startWith(false)),
  ]).pipe(
    map(
      ([small, newSiteNarrow, newSite]) =>
        small.matches || (newSite && newSiteNarrow.matches),
    ),
    distinctUntilChanged(),
    tap((isMobile) => {
      if (!isMobile && this.sidenav()?.opened) {
        this.sidenav()?.close();
      }
    }),
  );

  protected isMobile = toSignal(this.isMobile$, { initialValue: false });

  constructor() {
    effect(() => {
      const account = this.dealerAccount();
      if (account) {
        this.accountService.setPlatAccount(account);
        this.accountService.setApiUrl(environment.account_rest_api_server_url);
        this.accountService.setDeliveryApiUrl(
          environment.delivery_api_server_url,
        );
        this.accountService.setAnnouncementsApiUrl(
          environment.announcements_api_server_url,
        );
      }
    });
  }

  ngOnInit(): void {
    this.injectGoogleTagManagerConfig();
    console.log('Environment:', environment.envVersion);
  }

  private injectGoogleTagManagerConfig(): void {
    const gtmContainerId = environment.googleTagManager.containerId;
    const script = document.createElement('script');

    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmContainerId}');`;
    document.head.prepend(script);

    let noscriptElement = document.createElement('noscript');
    noscriptElement.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmContainerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.prepend(noscriptElement);
  }

  protected onSignOut(): void {
    this.authService.signOut();
  }
}
